/**
 * EntityCoveragePanel — shared-types Zod schemas, grouped by covered state.
 *
 * Extracted from AiUsageDashboard.tsx:1119-1248. Correction applied on
 * extraction: the covered/uncovered and kind <select> filters become
 * SearchableDropdown.
 */
import React, { useState } from 'react';
import { CheckCircle2, CircleDashed, Network } from 'lucide-react';
import SearchableDropdown from '../../../components/SearchableDropdown';
import type { ToolCatalogEntity } from '../../../services/aiControlApi';

const ENTITY_PAGE_SIZE = 30;

const EntityRow: React.FC<{ entity: ToolCatalogEntity }> = ({ entity }) => {
  const kindColor: Record<string, string> = {
    object: 'bg-blue-100 text-blue-700',
    enum:   'bg-rose-100 text-rose-700',
    union:  'bg-amber-100 text-amber-700',
    other:  'bg-gray-100 text-gray-700',
  };
  return (
    <tr className="hover:bg-stone-50">
      <td className="px-3 py-2 align-top">
        {entity.covered
          ? <CheckCircle2 size={14} className="text-emerald-500" />
          : <CircleDashed size={14} className="text-gray-300" />}
      </td>
      <td className="px-3 py-2 text-xs font-mono text-gray-800">{entity.name}</td>
      <td className="px-3 py-2 text-[11px] text-gray-500 hidden md:table-cell">
        <span className="truncate block max-w-xs">{entity.sourceFile}</span>
      </td>
      <td className="px-3 py-2 text-center">
        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${kindColor[entity.kind] || kindColor.other}`}>
          {entity.kind}
        </span>
      </td>
      <td className="px-3 py-2 text-right text-xs text-gray-700">{entity.fieldCount}</td>
    </tr>
  );
};

const EntityCoveragePanel: React.FC<{ entities: ToolCatalogEntity[] }> = ({ entities }) => {
  const [filter, setFilter] = useState<'all' | 'covered' | 'uncovered'>('uncovered');
  const [kindFilter, setKindFilter] = useState<string>('');
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);

  const filtered = entities.filter((e) => {
    if (filter === 'covered' && !e.covered) return false;
    if (filter === 'uncovered' && e.covered) return false;
    if (kindFilter && e.kind !== kindFilter) return false;
    if (q && !e.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    // Uncovered first, then by field count desc (fatter entities first)
    if (a.covered !== b.covered) return a.covered ? 1 : -1;
    return b.fieldCount - a.fieldCount;
  });
  const visible = showAll ? sorted : sorted.slice(0, ENTITY_PAGE_SIZE);

  const coveredCount = entities.filter((e) => e.covered).length;
  const kinds = Array.from(new Set(entities.map((e) => e.kind))).sort();

  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Network size={14} className="text-blue-600" />
            Data Entities (shared-types)
            <span className="text-[10px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded font-medium">
              {coveredCount}/{entities.length} covered
            </span>
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Zod schemas in <code className="bg-stone-100 px-1 rounded text-[10px]">packages/shared-types</code>.
            "Covered" = at least one active tool whose path references the entity (heuristic).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Filter name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="px-2 py-1.5 min-h-[44px] text-xs border border-stone-200 rounded-lg bg-white w-36"
          />
          <div className="w-44">
            <SearchableDropdown
              options={[
                { value: 'all', label: `All (${entities.length})` },
                { value: 'uncovered', label: `Uncovered (${entities.length - coveredCount})` },
                { value: 'covered', label: `Covered (${coveredCount})` },
              ]}
              value={filter}
              onChange={(v) => setFilter((v as any) || 'all')}
              placeholder="Coverage filter"
              searchPlaceholder="Search…"
              compact
            />
          </div>
          <div className="w-36">
            <SearchableDropdown
              options={[{ value: '', label: 'All kinds' }, ...kinds.map((k) => ({ value: k, label: k }))]}
              value={kindFilter || ''}
              onChange={(v) => setKindFilter(v || '')}
              placeholder="All kinds"
              searchPlaceholder="Search kinds…"
              compact
            />
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">No entities match the current filters.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide w-6"></th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Entity</th>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide hidden md:table-cell">Source</th>
                <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wide">Kind</th>
                <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Fields</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.map((e) => <EntityRow key={e.name} entity={e} />)}
            </tbody>
          </table>
          {sorted.length > ENTITY_PAGE_SIZE && (
            <div className="px-3 py-2 text-center border-t border-stone-100">
              <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="min-h-[44px] px-3 text-[11px] text-blue-600 hover:underline font-medium"
              >
                {showAll ? `Show top ${ENTITY_PAGE_SIZE} only` : `Show all ${sorted.length} entities`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EntityCoveragePanel;
